import { Badge } from '@synoptic/ui-kit/badge/badge.js';
import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { CaretArrowDownIcon } from '@synoptic/ui-kit/icons/react/caret-arrow-down.js';
import { CaretArrowUpIcon } from '@synoptic/ui-kit/icons/react/caret-arrow-up.js';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.js';
import clsx from 'clsx';
import { useState } from 'react';
import { TournamentBannerFragmentFragment } from '@/__generated__/graphql.ts';
import { S3TournamentImg } from '@/components/s3/tournament/s3-img.tsx';
import { getTournamentPrizePool } from '@/components/tournaments/get-tournament-prize-pool.ts';
import { MoneyBadge } from '@/components/tournaments/money-badge.tsx';
import { Link } from '@/routes-utils/navigation';
import { paths } from '@/routes-utils/paths';
import { TournamentMedals } from '@/routes/_app.p.tournaments_.$id/tournament-medal/tournament-medals.tsx';
import { TournamentCta } from '../tournament-cta';
import { TournamentStatus } from '../tournament-status/tournament-status';
import {
  bannerBody,
  bannerContainer,
  bannerContent,
  bannerFooter,
  bannerFooterContainer,
  bannerFooterWithPrize,
  bannerImg,
  bannerImgContainer,
  bannerInfo,
  bannerLabels,
  bannerManagedBy,
  bannerManagedByLink,
  bannerMoneyBadgeContainer,
  bannerShowMoreButton,
  bannerTitle,
  bannerTopContainer,
  bannerTournamentMedals,
} from './tournament-banner.css';

type TournamentBannerProps = {
  tournament: TournamentBannerFragmentFragment;
  withCta: boolean;
};
export const TournamentBanner: React.FC<TournamentBannerProps> = ({
  tournament,
  withCta,
}) => {
  const [showMore, setShowMore] = useState<boolean>(false);
  const money = getTournamentPrizePool(tournament.medals);

  const isTablet = useIsTablet();

  const imgSize = isTablet
    ? {
        w: 423,
        h: 282,
      }
    : {
        w: 714,
        h: 306,
      };

  return (
    <div className={bannerTopContainer}>
      <div className={bannerContainer}>
        <div className={bannerImgContainer}>
          {tournament.image ? (
            <S3TournamentImg
              className={bannerImg}
              s3Key={tournament.image.key}
              tournamentId={tournament.id}
              {...imgSize}
            />
          ) : null}
        </div>

        <div className={bannerContent}>
          <div className={bannerInfo}>
            <TournamentStatus tournamentId={tournament.id} />
            {tournament.managedBy ? (
              <div className={bannerManagedBy}>
                Created by{' '}
                <Link
                  className={bannerManagedByLink}
                  to={paths.makeUser(tournament.managedBy.username)}
                >
                  {tournament.managedBy.username}
                </Link>
              </div>
            ) : null}

            <div className={bannerTitle}>{tournament.name}</div>
            <div className={bannerBody}>{tournament.headline}</div>

            <div className={bannerLabels}>
              {tournament.category ? (
                <Badge>{tournament.category}</Badge>
              ) : null}
            </div>
          </div>

          <div className={clsx(bannerFooter, money && bannerFooterWithPrize)}>
            {money ? (
              <MoneyBadge
                className={bannerMoneyBadgeContainer}
                text={`Prize pool: ${money.toString()} USD`}
              />
            ) : null}
            {withCta && <TournamentCta />}
          </div>
        </div>
      </div>

      {tournament.medals.length ? (
        <>
          {showMore && (
            <div className={bannerTournamentMedals}>
              <TournamentMedals medals={tournament.medals} />
            </div>
          )}
          <div className={bannerFooterContainer}>
            <Button
              variant="ghost"
              endIcon={
                !showMore ? <CaretArrowDownIcon /> : <CaretArrowUpIcon />
              }
              className={bannerShowMoreButton}
              onClick={() => setShowMore((prevState) => !prevState)}
            >
              {!showMore ? 'Show all prizes' : 'Hide all prizes'}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
};
